import { Component, ElementRef, OnChanges, OnDestroy, OnInit, SimpleChanges, inject, input, output, viewChild } from '@angular/core';
import * as pbi from 'powerbi-client';
import * as models from 'powerbi-models';

import { PowerBiService } from './powerbi.service';

const enum TokenType {
  Aad = 'Aad',
  Embed = 'Embed',
}

const enum ReportType {
  Dashboard = 'Dashboard',
  Report = 'Report',
  Tile = 'Tile',
}

@Component({
  selector: 'lib-powerbi',
  styleUrls: ['powerbi.component.scss'],
  templateUrl: 'powerbi.component.html',
})
export class PowerBiComponent implements OnInit, OnChanges, OnDestroy {
  private readonly powerBiService = inject(PowerBiService);

  public readonly accessToken = input.required<string>();
  public readonly tokenType = input(TokenType.Embed);
  public readonly embedUrl = input.required<string>();
  public readonly id = input.required<string>();
  public readonly type = input.required<'dashboard' | 'report' | 'tile'>();
  public readonly name = input<string>();
  public readonly options = input.required<models.ISettings>();
  public readonly embedded = output<number>();
  public readonly powerBiIFrameRef = viewChild.required<ElementRef>('powerBiIFrame');

  private component: pbi.Embed | null = null;

  public ngOnInit() {
    const powerBiIFrameRef = this.powerBiIFrameRef();
    if (powerBiIFrameRef.nativeElement && this.validateRequiredAttributes()) {
      this.embed(powerBiIFrameRef.nativeElement, this.getConfig());
    }
  }

  public ngOnChanges(changes: SimpleChanges) {
    const { accessToken, tokenType, embedUrl, id, type, name, options } = changes;

    if (this.validateRequiredAttributes()) {
      const config = this.getConfig(
        accessToken?.currentValue,
        tokenType?.currentValue,
        embedUrl?.currentValue,
        id?.currentValue,
        type?.currentValue,
        name?.currentValue,
        options?.currentValue,
      );
      this.embed(this.powerBiIFrameRef().nativeElement, config);
    } else if (this.component) {
      this.reset(this.powerBiIFrameRef().nativeElement);
    }
  }

  public ngOnDestroy() {
    if (this.component) {
      this.reset(this.powerBiIFrameRef().nativeElement);
    }
  }

  private validateRequiredAttributes(): boolean {
    const embedUrl = this.embedUrl();
    const accessToken = this.accessToken();
    return typeof embedUrl === 'string' && embedUrl.length > 0 && typeof accessToken === 'string' && accessToken.length > 0;
  }

  private getTokenType(tokenType: TokenType): models.TokenType {
    switch (tokenType) {
      case TokenType.Aad:
        return models.TokenType.Aad;
      case TokenType.Embed:
      default:
        return models.TokenType.Embed;
    }
  }

  private getConfig(
    accessToken?: string,
    tokenType?: TokenType,
    embedUrl?: string,
    id?: string,
    type?: 'dashboard' | 'report' | 'tile',
    name?: string,
    options?: models.ISettings,
  ): pbi.IEmbedConfiguration {
    return {
      type: type ?? this.type() ?? ReportType.Report,
      embedUrl: embedUrl ?? this.embedUrl(),
      accessToken: accessToken ?? this.accessToken(),
      tokenType: this.getTokenType(tokenType ?? this.tokenType()),
      id: id ?? this.id(),
      uniqueId: name ?? this.name(),
      settings: options ?? this.options(),
    };
  }

  public reset(element: HTMLElement) {
    this.powerBiService.reset(element);
    this.component = null;
  }

  private embed(element: HTMLElement, config: pbi.IEmbedConfiguration) {
    this.component = this.powerBiService.embed(element, config);
    this.embedded.emit(this.component as any);
  }
}
